/**
 * BatchGeoDrawingManager handles the drawing and selection tools on maps such
 * as the tool to select a group of markers or a ruler to measure distances.
 *
 * Basic Usage (will load and initialize the drawing manager on `window.map`):
 *
 * window.batchGeoDrawingManager = new BatchGeoDrawingManager({
 *   map: window.map,
 *   tools: {
 *     'hand': BatchGeoDrawingManagerHand,
 *     "rectangle": BatchgeoDrawingManagerRectangle
 *   }
 * });
 *
 * Additional options you can pass are:
 *
 * - `initialMode`: you could pass `rectangle` for example if you wanted the
 *                  first mode to be rectangle instead of the hand (default).
 * - `toolOptions`: A hash where you can pass options to override default Google
 *                  shape options. For example if you wanted to change the color
 *                  of the stroke on rectangles you could pass something like:
 *                  new BatchGeoDrawingManager({
 *                    toolOptions: {
 *                      rectangle: { strokeColor: '#000' }
 *                    }
 *                  })
 *
 * Adding new tools:
 * To add a new tool to the DrawingManager you pass it into the `tools` object.
 * The tool itself should be a component with three required properties:
 * - enable()
 * - disable()
 * - button
 *
 * The enable() and disable() methods are called when the tool is enabled or
 * disabled. Use enable() to setup your tool and disable() to tear it down. The
 * buttons property is an object hash that should contain these properties:
 *
 * - `title`: the HTML title attribute that will be applied
 * - `order`: the placement of the button on the toolbar
 * - `class`: a class string that will be concatenated onto the button classes
 * - `mode`: the "mode" to enable when you click this. For example, if you want
 *           the button to trigger the "hand" mode set in your tools: {} setup
 *           you'd set mode to "hand"
 *
 *
 * Checkout the BatchGeoDrawingManagerHand component for a good absolute
 * barebones drawing manager tool.
 */
(function (window) {
  function BatchGeoDrawingManager(opts) {
    opts = opts || {};

    this.settings = $.extend(
      {
        // The first tool to load
        initialMode: "hand",
        // A stub. If you wanted to alter Google's Rectangle options you
        // could pass in `rectangle` here and pass in options like fillColor
        toolOptions: {},
        shapeOptions: {
          editable: false,
          draggable: false,
          fillColor: "#4285F4",
          strokeColor: "#4285F4",
          fillOpacity: 0.05,
          strokeWeight: 1,
          zIndex: 999,
        },
      },
      opts,
    );

    this._initializeDrawingManager();
    this._allowPanningWithSpaceKey();
    this._setModeDispatcher(this.settings.initialMode);
  }

  /**
   * Calls the BGS dispatch event to change the drawing mode
   *
   * @param {string} mode A string version of the mode type (i.e. "hand")
   * @return {undefined}
   */
  BatchGeoDrawingManager.prototype._setModeDispatcher = function (mode) {
    BatchGeoStore.dispatch({
      type: "DRAWING_MANAGER_SET_MODE",
      mode: mode,
    });
  };

  /**
   * Sets up a BGS listener for drawing mode changes.
   *
   * It also sets up drawing events for tools to listen to which are:
   * - batchgeo.drawingmanager.start
   * - batchgeo.drawingmanager.change
   * - batchgeo.drawingmanager.end
   *
   * These happen when you would expect. Start when you start a draw
   * (mousedown), change when the draw is being changed (mousemove) and end
   * when the draw is finished (mouseup).
   *
   * @return {undefined}
   */
  BatchGeoDrawingManager.prototype._initializeDrawingManager = function () {
    var self = this;
    var $mapDiv = $(self.settings.map.getDiv());

    // Setup a BGS listener for drawing mode changes
    this._bgsListener = BatchGeoStore.addListener(
      function (state) {
        return state.DrawingManager.mode;
      },
      function (newMode, oldMode) {
        return newMode !== oldMode;
      },
      function (newMode, oldMode) {
        self._setDrawingMode(newMode, oldMode);
      },
    );

    this._mouseDownHandler = function (event) {
      const mode = BatchGeoStore.getState().DrawingManager.mode;
      if (mode !== "polygon" && window.multiClickDraw) {
        return;
      }

      var isDraggable = self.settings.tools[mode].draggable;
      var startPosition = { x: event.clientX, y: event.clientY };
      var mouseMoveHandler = function (event) {
        if (!isDraggable) {
          $(window).trigger("batchgeo.drawingmanager.change", [event]);
        } else if (
          Math.abs(startPosition.x - event.clientX) > 5 ||
          Math.abs(startPosition.y - event.clientY) > 5
        ) {
          // If we're here the user has started dragging and if the
          // has hasStartedDragging has NOT been set to true yet we
          // need to trigger the draw start handler and set this var
          // to true to prevent it from being fired again.
          if (!window.hasStartedDragging) {
            window.hasStartedDragging = true;
            $(window).trigger("batchgeo.drawingmanager.start", [event]);
          }

          $(window).trigger("batchgeo.drawingmanager.change", [event]);
        }
      };

      var mouseUpHandler = function (event) {
        const mode = BatchGeoStore.getState().DrawingManager.mode;
        if (
          mode !== "polygon" &&
          !window.hasStartedDragging &&
          !window.multiClickDraw
        ) {
          window.multiClickDraw = true;
          return;
        }
        window.multiClickDraw = false;
        window.hasStartedDragging = false;
        $(window).trigger("batchgeo.drawingmanager.end", [event]);
        $mapDiv.off("mousemove.batchgeodrawingmanager", mouseMoveHandler);
        $(window).off("mouseup.batchgeodrawingmanager", mouseUpHandler);
        if (self._panTimer) {
          clearInterval(self._panTimer);
        }
      };

      if (!isDraggable) {
        $(window).trigger("batchgeo.drawingmanager.start", [event]);
      }

      $mapDiv.on("mousemove.batchgeodrawingmanager", mouseMoveHandler);
      $(window).on("mouseup.batchgeodrawingmanager", mouseUpHandler);
    };

    $mapDiv.on("mousedown.batchgeodrawingmanager", this._mouseDownHandler);
  };

  /**
   * Allows the map to be dragged with the space bar even if there is
   * another tool selected.
   *
   * @return {undefined}
   */

  BatchGeoDrawingManager.prototype._allowPanningWithSpaceKey = function () {
    var self = this;
    var space = false;
    var cacheMode;

    $(window)
      .keyup(function (evt) {
        if (evt.keyCode == 32) {
          space = false;
          self._setModeDispatcher(cacheMode);
        }
      })
      .keydown(function (evt) {
        if (evt.keyCode == 32) {
          if (!space) {
            cacheMode = self.getDrawingMode();
          }
          space = true;
          self._setModeDispatcher("hand");
        }
      });
  };

  /**
   * Given and x,y values it will figure out the LatLng on the map that x,y
   * pixel value translates to.
   *
   * @param  {number} x the x value in pixels of the location to check
   * @param  {number} y the y value in pixels of the location to check
   * @return {object}   A new google LatLng instance
   */
  BatchGeoDrawingManager.prototype.fromPixelToLatLng = function (x, y) {
    var map = this.settings.map;
    var projection = map.getProjection();
    var topRight = projection.fromLatLngToPoint(map.getBounds().getNorthEast());
    var bottomLeft = projection.fromLatLngToPoint(
      map.getBounds().getSouthWest(),
    );
    var scale = 1 << map.getZoom();
    return projection.fromPointToLatLng(
      new google.maps.Point(x / scale + bottomLeft.x, y / scale + topRight.y),
    );
  };

  /**
   * Returns an accurate offsetX/Y. IE9-10 don't accurately report the correct
   * offsetX/Y so we need to use clientX/Y and manually build the offset from
   * the map div.
   *
   * @param  {object} event The event to build the offsetX/Y from
   * @return {object}       Returns an object with `x` and `y` keys
   */
  BatchGeoDrawingManager.prototype.getEventOffsets = function (event) {
    var rect = this.settings.map.getDiv().getBoundingClientRect();

    return {
      x: event.clientX - rect.left,
      y: event.clientY - rect.top,
    };
  };

  /**
   * Same as fromPixelToLatLng except you can just pass an event object and it
   * will figure out where on the map in LatLng coordinates the event happened
   *
   * @param  {object} event An event object containing offsetX/Y
   * @return {object}       A new google LatLng instance
   */
  BatchGeoDrawingManager.prototype.fromEventToLatLng = function (event) {
    var offset = this.getEventOffsets(event);
    return this.fromPixelToLatLng(offset.x, offset.y);
  };

  /**
   * Returns a string of the current Google Maps unit scale. It will
   * return one these:
   * - km
   * - m
   * - mi
   * - ft
   *
   * @return {string} A string of the unit type from the list above
   */
  BatchGeoDrawingManager.prototype.getDistanceScale = function () {
    return window.batchGeoGoogleMapsManager.getDistanceScale();
  };

  /**
   * Given two Google Map's LatLng points it will return the distance between
   * those two points. The distance is returned, by default, in the units the
   * map is set to. So if the user is zoomed super far in it'll be m or ft or
   * zoomed out km or mi. You can override the auto distance scaling by
   * passing a third optional param as a string in the scaling you want it to
   * return. Supported types are km, m, mi, and ft.
   *
   * @param  {object}   p1 A google maps LatLng object
   * @param  {object}   p2 A google maps LatLng object
   * @param  {string}   [distanceScale] The scaling you want it to return. If
   *                    not set it will set it with #getDistanceScale.
   * @return {number}   The distance in km (_not_ rounded)
   */
  BatchGeoDrawingManager.prototype.distanceBetweenTwoLatLng = function (
    p1,
    p2,
    distanceScale,
  ) {
    distanceScale = distanceScale || this.getDistanceScale();

    var rad = function (x) {
      return (x * Math.PI) / 180;
    };

    var earthRadius = {
      km: 6371,
      m: 6371 * 1000, // radius in meters times meters in a kilometer
      mi: 3959,
      ft: 3959 * 5280, // radius in miles times feet in a mile
    };

    var R = earthRadius[distanceScale];
    var dLat = rad(p2.lat() - p1.lat());
    var dLong = rad(p2.lng() - p1.lng());
    var a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(rad(p1.lat())) *
        Math.cos(rad(p2.lat())) *
        Math.sin(dLong / 2) *
        Math.sin(dLong / 2);
    var c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    var d = R * c;
    return d;
  };

  /**
   * Changes the drawing mode to the one passed in by disabling the previous
   * mode (if one was set) and enabling the new one as well as setting and
   * removing the "active" class from the corresponding toolbar button.
   *
   * @param {string} mode The mode (like "hand", "rectangle", etc)
   * @return {undefined}
   */
  BatchGeoDrawingManager.prototype._setDrawingMode = function (
    newMode,
    oldMode,
  ) {
    if (!this.settings.tools) return;

    // For disable AND enable we pass *this* DrawingManager's instance so
    // that the tools have access to DrawingManager APIs.
    if (oldMode && this.settings.tools[oldMode]) {
      this.settings.tools[oldMode].disable(this);
    }

    // Unlike disable though, we pass the tool options here as the 2nd arg
    if (newMode && this.settings.tools[newMode]) {
      this.settings.tools[newMode].enable(
        this,
        this.settings.toolOptions[newMode],
      );
    }
  };

  /**
   * Does what it says ;) It's a helper function to get the drawing mode
   *
   * @return {string} The current mode that is set
   */
  BatchGeoDrawingManager.prototype.getDrawingMode = function () {
    return BatchGeoStore.getState().DrawingManager.mode;
  };

  /**
   * Clears the map for drawing tools. Call this to remove infoBoxes and such.
   * @return {undefined}
   */
  BatchGeoDrawingManager.prototype.clearMap = function () {
    window.batchGeoGoogleMapsMarker.closeMarker();
  };

  /**
   * Turns on map panning when the mouse gets near to the edge of the map.
   * Other tools can call this to enable it (and disable with another method)
   * so all tools have the same map panning behavior and feel.
   *
   * @return {undefined}
   */
  BatchGeoDrawingManager.prototype.enableMapPanning = function () {
    var self = this;
    // Only enable once
    if (this._googleMouseMoveMapPanHandler) return;
    this._googleMouseMoveMapPanHandler = google.maps.event.addListenerOnce(
      this.settings.map,
      "mousemove",
      function (event) {
        self._mapPanHandler(event);
      },
    );
  };

  /**
   * Remove map panning when the user is near the edge of the map.
   *
   * @return {undefined}
   */
  BatchGeoDrawingManager.prototype.disableMapPanning = function () {
    clearInterval(this._panTimer);
    google.maps.event.removeListener(this._googleMouseMoveMapPanHandler);
    delete this._googleMouseMoveMapPanHandler;
  };

  /**
   * This pans the map when the user is moused down and at the end of the map
   * bounding box.
   *
   * @return {undefined}
   */
  BatchGeoDrawingManager.prototype._mapPanHandler = function () {
    // FIXME: This was throwing errors and not working. Added early return prevent sentry exploding
    return;
  };

  /**
   * Removes the the drawing manager instance by removing the buttons and
   * removing the the mouseDownHandler.
   *
   * @return {undefined}
   */
  BatchGeoDrawingManager.prototype.destroy = function () {
    BatchGeoStore.removeListener(this._bgsListener);
    $(this.settings.map.getDiv()).off(
      "mousedown.batchgeodrawingmanager",
      this._mouseDownHandler,
    );
  };

  window.BatchGeoDrawingManager = BatchGeoDrawingManager;
})(window);
